import React, { useState, useEffect } from 'react';
import api from '../../utils/axiosConfig';
import { useNavigate } from 'react-router-dom';
import PageLoadingArea from '../../components/PageLoader/PageLoadingArea';
import usePageLoading from '../../hooks/usePageLoading';
import '../../styles/ListPage.css';

function ClientList() {
    const navigate = useNavigate();
    const { isLoading, withLoading } = usePageLoading(true);
    const [clients, setClients] = useState([]);
    const [selectedClients, setSelectedClients] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredClients, setFilteredClients] = useState([]);

    useEffect(() => {
        withLoading(async () => {
            const res = await api.get('/clients');
            setClients(res.data);
            setFilteredClients(res.data);
        }).catch((err) => console.log(err));
    }, [withLoading]);

    useEffect(() => {
        const results = clients.filter(client =>
            (client.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (client.panNo || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (client.gstNo || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (client.address || '').toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredClients(results);
    }, [searchTerm, clients]);

    const handleCheckboxChange = (clientId) => {
        setSelectedClients(prev =>
            prev.includes(clientId) ? prev.filter(id => id !== clientId) : [...prev, clientId]
        );
    };

    const handleSelectAll = (e) => {
        setSelectedClients(e.target.checked ? filteredClients.map(client => client.id) : []);
    };

    const handleDeleteSelected = () => {
        if (!window.confirm('Are you sure you want to delete the selected clients?')) return;
        Promise.all(selectedClients.map(clientId => api.delete(`/clients/${clientId}`)))
            .then(() => {
                setClients(prev => prev.filter(client => !selectedClients.includes(client.id)));
                setSelectedClients([]);
            })
            .catch(err => {
                if (err.response && err.response.status === 400) {
                    alert('Cannot delete clients with associated bills');
                } else {
                    alert('Error deleting clients');
                }
            });
    };

    const allSelected = filteredClients.length > 0 && selectedClients.length === filteredClients.length;

    return (
        <PageLoadingArea isLoading={isLoading}>
        <div className="list-page">
            <div className="list-page-header">
                <div className="list-page-title">
                    <h1>Clients</h1>
                    <p className="list-page-subtitle">
                        {filteredClients.length} client{filteredClients.length === 1 ? '' : 's'}
                    </p>
                </div>
                <div className="list-header-actions">
                    <div className="list-search-wrap">
                        <input
                            type="text"
                            placeholder="Search name, PAN, GST, or address"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="list-search-input"
                        />
                    </div>
                    <div className="list-button-group">
                        <button
                            type="button"
                            onClick={() => navigate('/admin')}
                            className="list-secondary-button"
                        >
                            Back to admin
                        </button>
                        <button type="button" onClick={() => navigate('/createClient')} className="list-add-button">
                            Add New Client
                        </button>
                        {selectedClients.length > 0 && (
                            <button type="button" onClick={handleDeleteSelected} className="list-delete-button">
                                Delete ({selectedClients.length})
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="list-table-card">
                <div className="list-table-scroll">
                    <table className="list-table">
                        <thead>
                            <tr>
                                <th className="col-check">
                                    <input type="checkbox" checked={allSelected} onChange={handleSelectAll} aria-label="Select all" />
                                </th>
                                <th>Sr No</th>
                                <th>Name</th>
                                <th>Address</th>
                                <th>PAN No</th>
                                <th>GST No</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredClients.length === 0 ? (
                                <tr className="list-empty-row">
                                    <td colSpan="6">
                                        <div className="list-empty-state">
                                            <h3>No clients found</h3>
                                            <p>{searchTerm ? 'Try a different search term.' : 'Add your first client to get started.'}</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredClients.map((client, index) => (
                                <tr
                                    key={client.id}
                                    className={`list-row${selectedClients.includes(client.id) ? ' is-selected' : ''}`}
                                    onClick={(e) => {
                                        if (e.target.type !== 'checkbox') navigate(`/createClient/${client.id}`);
                                    }}
                                >
                                    <td className="col-check" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            checked={selectedClients.includes(client.id)}
                                            onChange={() => handleCheckboxChange(client.id)}
                                        />
                                    </td>
                                    <td>{index + 1}</td>
                                    <td><span className="list-primary-text">{client.name}</span></td>
                                    <td>{client.address || '—'}</td>
                                    <td>{client.panNo}</td>
                                    <td>{client.gstNo}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
        </PageLoadingArea>
    );
}

export default ClientList;
